/// <reference types="vite/client" />
import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL as string
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string

if (!url || !key) throw new Error('Missing Supabase env vars — copy .env.example to .env.local')

export const supabase = createClient(url, key)

// ── domain types ─────────────────────────────────────────────────────────────

/** 'trialing' until a household first subscribes; the rest are Stripe's.
 *  What a household may do comes from this *and* plan_expires_at — see
 *  householdAccess() in lib/billing.ts. */
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid'

export interface Household {
  id: string
  name: string
  invite_code: string
  created_at: string
  subscription_status: SubscriptionStatus
  /** The trial's end until a subscription exists, then the paid-through date —
   *  the webhook advances it only while Stripe says 'active'
   *  (031_household_plan.sql). Plus runs three days past it, then the household is on Free (042). */
  plan_expires_at: string
  /** Set by the Stripe webhook; display-only ("renews on <date>"). */
  current_period_end: string | null
  cancel_at_period_end: boolean
  /** The member who pays — set by the billing webhook. Only this member may
   *  open the Stripe portal (025_payer_identity.sql). Null on a household with
   *  no subscription yet; unreadable columns (the Stripe ids) come back absent
   *  under the column-scoped SELECT grant (024). */
  payer_member_id: string | null
}

/**
 * The households columns a member may read. 024_grant_hardening.sql column-scoped
 * the SELECT grant so the Stripe object ids stop leaking to every member — and a
 * column-scoped grant means `select=*` is a permission error (Postgres requires
 * SELECT on every column for `*`), so every households read must name its columns.
 */
export const HOUSEHOLD_COLUMNS =
  'id,name,invite_code,created_at,subscription_status,plan_expires_at,' +
  'current_period_end,cancel_at_period_end,payer_member_id'

export interface Member {
  id: string
  household_id: string
  display_name: string
  auth_user_id: string | null
  created_at: string
}

export interface Category {
  id: string
  household_id: string
  name: string
  icon: string | null
  sort_order: number
}

/** One ingredient of a saved recipe, as reviewed in the import sheet. */
export interface RecipeIngredient {
  name: string
  amount: number | null
  unit: string | null
  /** Unticked at import — the household usually already has it in. Kept so the
   *  recipe is the whole dish, but never counted as missing. Absent on recipes
   *  saved before pantry ingredients were kept. */
  pantry?: boolean
  /** The category a member picked for it (recipe editor, import review). Absent = the
   *  household's guess, read live (lib/recipeBook.ts `ingredientCategory`), so it keeps
   *  following the category memory. */
  category_id?: string | null
}

export type RecipeSource = 'web' | 'youtube' | 'tiktok' | 'instagram'

export interface Recipe {
  id: string
  household_id: string
  title: string
  url: string | null
  /** Portions the stored amounts are written for; null when the source didn't say. */
  servings: number | null
  ingredients: RecipeIngredient[]
  /** Total time in minutes from the source page; null when it didn't say. */
  minutes: number | null
  /** Starred and tags are the household's, shared by every member. */
  starred: boolean
  tags: string[]
  /** A member's override; null = guessed from the ingredients (lib/vegetarian.ts). */
  vegetarian: boolean | null
  steps: RecipeStep[]
  /** The photo in the private recipe-photos bucket ("<household>/<file>.jpg", 035); null = the generated cover. */
  photo_path: string | null
  /** The photo is a copy of the page's image or the video's thumbnail, not a member's (039). */
  photo_imported: boolean
  /** Where it was imported from (039); null when written by hand. */
  source: RecipeSource | null
  /** The page's own rating out of 5 and how many rated it; both null when it has none (039). */
  rating_value: number | null
  rating_count: number | null
  created_by: string | null
  updated_at: string
  created_at: string
}

/** One method step. Which ingredients it uses is inferred (lib/stepUses.ts), not stored. */
export interface RecipeStep {
  text: string
  /** A timer for this step, parsed from its text at import; null when it has none. */
  minutes: number | null
}

/** A week of meals saved under a name, to put on any week again (036). Hard-deleted. */
export interface MealTemplate {
  id: string
  household_id: string
  name: string
  /** weekday 0 = Monday. A recipe deleted since stays here and is skipped when applied. */
  meals: { weekday: number; recipe_id: string; servings: number }[]
  created_by: string | null
  created_at: string
  updated_at: string
}

/** A store the household shops at, with the walking order learned there (037). Hard-deleted. */
export interface Store {
  id: string
  household_id: string
  name: string
  /** Item keys (lib/normalize itemKey) in the order they are usually picked (lib/storeRoute.ts). */
  route: string[]
  created_at: string
  updated_at: string
}

/** One shopping trip, started in shopping mode and finished by Finish trip (037). */
export interface Trip {
  id: string
  household_id: string
  store_id: string | null
  started_by: string | null
  started_at: string
  finished_at: string | null
  /** Which device ended it (038): a trip finished on the watch asks its cost on the phone, once. */
  finished_on: 'phone' | 'watch' | null
  /** Who pressed Finish (045): the summary is theirs; everyone else shopping gets a note. */
  finished_by: string | null
  items: number | null
  /** "What did it cost?", in `currency` (ISO 4217, upper case); both null when skipped. */
  cost: number | null
  currency: string | null
  updated_at: string
  /** The order walked, put by hand in Stats (046): item ids. Null: the order things went in. */
  picked_order?: string[] | null
  /** Deleted from Stats (046): no trip any more, its rows still history. */
  deleted_at?: string | null
}

/** Someone shopping in a trip (045): written only by start_trip / leave_trip. */
export interface TripShopper {
  trip_id: string
  member_id: string
  household_id: string
  joined_at: string
  /** Left without finishing (Leave trip); joining again clears it. */
  left_at: string | null
}

/** A recipe on a day of the household's week plan (the Plan tab). Hard-deleted. */
export interface PlannedMeal {
  id: string
  household_id: string
  /** Local calendar date, "YYYY-MM-DD". */
  day: string
  recipe_id: string
  servings: number
  /** Its ingredients went to the list from the plan. */
  added: boolean
  cooked: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface Item {
  id: string
  household_id: string
  category_id: string | null
  name: string
  quantity: string | null
  note: string | null
  done: boolean
  done_at: string | null
  /** Who put it in the basket (046), set by the database from the caller. */
  done_by?: string | null
  created_by: string | null
  sort_order: number
  updated_at: string
  created_at: string
  deleted_at: string | null
}
